import Button from '@/component/Button'
import Form, { Field } from '@/component/FieldForm'
import Toast from '@/component/Toast'
import TGASDK from '@/config/TGASDK'
import AuthContext from '@/context/AuthContext'
import { PopUpStore } from '@/store/PopupStore'
import { RESPONSE_CODES } from '@kulee/tga-constant'
import { UC } from '@kulee/tga-sdk'
import { useMutation } from '@tanstack/react-query'
import React, { useContext, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import EmailInput from '../../component/EmailInput'
import PasswordInput from '../../component/PasswordInput'

const LoginMain: React.FC<PropType> = () => {
  const { t } = useTranslation('uc')
  const { init } = useContext(AuthContext)

  const mutation = useMutation({
    mutationFn: (values: FormValuesType) => {
      const { email, password } = values

      return TGASDK.module.UC.loginByAccount({
        account: email,
        password
      })
    }
  })

  const handleLogin = async (values: FormValuesType) => {
    mutation.mutate(values, {
      onSuccess: () => {
        init()
        PopUpStore.setLoginPopupVisible(false)
        Toast.show({
          content: t('message.loginSuccess'),
          icon: 'success'
        })
      },
      onError: (error: any) => {
        Toast.show({
          content:
            error.code === RESPONSE_CODES.UC_USER_NOT_EXIST
              ? t('message.loginAccountNotExistsFail')
              : error.code === RESPONSE_CODES.UC_PASSWORD_INVALID
                ? t('message.loginPasswordFail')
                : t('message.loginFail'),
          icon: 'error'
        })
      }
    })
  }

  const handleRegister = async () => {
    PopUpStore.setLoginPopupVisible(false)
    PopUpStore.setRegisterPopupVisible(true)
  }

  const handleResetPassword = () => {
    PopUpStore.setResetPasswordPopupVisible(true)
  }

  useEffect(() => {
    TGASDK.addModule(UC)
  }, [])

  return (
    <div className="flex flex-col gap-md">
      <div className="text-lg font-bold text-center">{t('tip.loginWelcome')}</div>
      <div>
        <Form onFinish={handleLogin}>
          {(values, form) => {
            const emailError = form.getFieldError('email')
            const passwordError = form.getFieldError('password')

            return (
              <div className="flex flex-col gap-sm">
                <div className="flex flex-col">
                  <Field<FormValuesType>
                    name="email"
                    rules={[
                      {
                        required: true,
                        pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                        message: t('message.emailFormatError')
                      }
                    ]}
                  >
                    <EmailInput placeholder={t('placeholder.email')} />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{emailError[0]}</p>
                </div>
                <div className="flex flex-col">
                  <Field<FormValuesType>
                    name="password"
                    rules={[
                      {
                        required: true,
                        pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
                        message: t('message.passwordFormatError')
                      }
                    ]}
                  >
                    <PasswordInput />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{passwordError[0]}</p>
                  <p className="h-4  px-sm">
                    <Button type="link" size="small" className="font-normal underline" onClick={handleResetPassword}>
                      {t('btn.forgetPassword')}
                    </Button>
                  </p>
                </div>
                <div className="mt-sm">
                  <Button htmlType="submit" type="primary" className="w-full" loading={mutation.isPending}>
                    {t('btn.login')}
                  </Button>
                </div>
              </div>
            )
          }}
        </Form>
      </div>
      <div className="text-center">
        <Trans
          i18nKey="tip.accountNone"
          ns="uc"
          components={[<Button type="link" className="font-normal underline" onClick={handleRegister} />]}
        />
      </div>
      <div className="border-t border-gray-300 text-gray-500 pt-md">
        <Trans
          i18nKey="tip.agreement"
          ns="uc"
          components={[<a href="#" className="underline" />, <a href="#" className="underline" />]}
        />
      </div>
    </div>
  )
}

interface PropType {}

interface FormValuesType {
  email: string
  password: string
}

export default LoginMain
